import { CommonButton, ErrorPage, Expandable, LoadingPage, TableInner } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CountryName } from '../../components/coutryName/CountryName';
import { useQueryListOneCompany } from '../../entities/companies/queries';
import { TGiftPackType, useQueryOneCompanyGiftPackages } from '../../entities/giftPackages/queries';
import { getReadableGiftPackageType } from '../../entities/giftPackages/shared';
import { TGiftPackageType, TProduct } from '../../generated/graphql';
import { GIFT_PACKAGES_PATH, GIFTS_PATH, PACKAGE_PATH } from '../../routes/paths';
import { CompanyModal } from './CompanyModal';
import styles from './CompanyPackages.module.scss';

type TPproductListProps = {
  rows: any;
  packageId: string;
};

export const ProductList = (props: TPproductListProps) => {
  const { rows, packageId } = props;
  const navigate = useNavigate();

  return (
    <>
      <CommonButton
        className={clsx(styles.addBtn)}
        leadingIcon='edit'
        label={`Edit Product Package`}
        onClick={() => {
          navigate(
            generatePath(`${GIFTS_PATH}/${GIFT_PACKAGES_PATH}/${PACKAGE_PATH}`, {
              packageId
            }),
            { state: { from: 'company' } }
          );
        }}
        variant='filled'
      />
      <div style={{ overflow: 'auto' }}>
        <TableInner<TProduct>
          enableSorting={false}
          defaultSorting={{ id: 'description', desc: false }}
          className={clsx(styles.tableContainer)}
          columns={[
            {
              header: 'Description',
              accessorKey: 'description',
              cell: ({ cell }) => <>{cell.row.original?.description ?? '(Product removed)'}</>
            },
            { header: 'HS Code', accessorKey: 'hsCode', cell: ({ cell }) => <>{cell.row.original?.hsCode ?? '-'}</> },
            { header: 'Quantity', accessorKey: 'quantity', cell: ({ cell }) => <>{cell.row.original?.quantity ?? '-'}</> },
            { header: 'Value', accessorKey: 'value', cell: ({ cell }) => <>{cell.row.original?.value ?? '-'}</> },
            {
              header: 'Country',
              accessorKey: 'country',
              cell: ({ cell }) => <CountryName name={cell.row.original?.country || undefined} />
            }
          ]}
          data={rows}
          emptyText='No products were found for this Gift Package.'
          virtual={false}
        />
      </div>
    </>
  );
};

export const CompanyPackages = () => {
  const { companyId = '0' } = useParams();

  const {
    data: company,
    loading,
    error
  } = useQueryListOneCompany({
    id: companyId
  });

  const { data: giftPackages, loading: loadingPackages, error: errorPackages } = useQueryOneCompanyGiftPackages({ companyId: company.id as string });

  const PackagesTable = ({ packs }: { packs?: null | TGiftPackType[] }) => {
    if (!packs || !packs.length) return null;
    const type = packs[0].type as TGiftPackageType;
    return (
      <>
        <div className={clsx(styles.packageTableContainer)}>
          <div className={clsx(styles.topContainer)}>
            <h2 className={clsx(styles.tableTitle)}>{getReadableGiftPackageType(type as TGiftPackageType)}</h2>
          </div>
          {packs.map((p) => {
            return (
              <Expandable key={p.id} className={styles.reviewExpandable} title={`${p.name}`}>
                <ProductList rows={p.hydratedProducts} packageId={p.id} />
              </Expandable>
            );
          })}
        </div>
      </>
    );
  };

  if (loading && loadingPackages)
    return (
      <CompanyModal headline='Company Gift Packages Configuration'>
        <LoadingPage />
      </CompanyModal>
    );

  if (error && errorPackages) {
    console.error('query Apollo ERROR:', error);
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (error.message) toast.error(error.message);
    if (!company)
      return (
        <CompanyModal headline='Company Gift Packages Configuration'>
          <ErrorPage error={error || errorPackages} />
        </CompanyModal>
      );
  }

  return (
    <>
      <CompanyModal headline='Company Gift Packages Configuration'>
        {company.employeeNewHireKitActivated && <PackagesTable packs={giftPackages.filter((pack) => pack.type == TGiftPackageType.NewHire)} />}
        {company.employeeWorkAnniversaryActivated && <PackagesTable packs={giftPackages.filter((pack) => pack.type == TGiftPackageType.WorkAnniversary)} />}
        {company.employeeBirthdayActivated && <PackagesTable packs={giftPackages.filter((pack) => pack.type == TGiftPackageType.EmployeeBirthday)} />}
        {company.significantOtherBirthdayActivated && <PackagesTable packs={giftPackages.filter((pack) => pack.type == TGiftPackageType.SpouseBirthday)} />}
        {company.childBirthdayActivated && <PackagesTable packs={giftPackages.filter((pack) => pack.type == TGiftPackageType.ChildBirthday)} />}
        {company.petBirthdayActivated && <PackagesTable packs={giftPackages.filter((pack) => pack.type == TGiftPackageType.PetBirthday)} />}
      </CompanyModal>
    </>
  );
};
